/**
 * Toasts: brief, non-blocking messages ("Saved", "Moved to trash — Undo").
 *
 * A tiny module-level store rather than a context, so any code — including non-React
 * modules like the save queue — can raise one with `toast(...)`. Radix announces each
 * one to screen readers politely, and pauses the timer while hovered or focused.
 */

import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { Toast as Primitive } from 'radix-ui';
import { useSyncExternalStore } from 'react';

import { cn } from './cn.ts';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: 'info' | 'success' | 'error';
  /** A single follow-up action, such as Undo. */
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

interface ToastEntry extends ToastOptions {
  id: number;
}

let entries: ToastEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function toast(options: ToastOptions): number {
  const id = nextId++;
  entries = [...entries.slice(-3), { ...options, id }];
  emit();
  return id;
}

export function dismissToast(id: number): void {
  entries = entries.filter((entry) => entry.id !== id);
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const ICONS = { info: Info, success: CircleCheck, error: CircleAlert };

export function Toaster() {
  const current = useSyncExternalStore(subscribe, () => entries);
  return (
    <Primitive.Provider swipeDirection="right">
      {current.map((entry) => {
        const tone = entry.tone ?? 'info';
        const Icon = ICONS[tone];
        return (
          <Primitive.Root
            key={entry.id}
            duration={entry.durationMs ?? (tone === 'error' ? 8000 : 4000)}
            type={tone === 'error' ? 'foreground' : 'background'}
            onOpenChange={(open) => {
              if (!open) dismissToast(entry.id);
            }}
            className="flex items-start gap-3 rounded-lg border border-border bg-surface-raised p-3 shadow-xl"
          >
            <Icon
              size={16}
              aria-hidden
              className={cn(
                'mt-0.5 shrink-0',
                tone === 'error'
                  ? 'text-danger'
                  : tone === 'success'
                    ? 'text-success'
                    : 'text-accent-text',
              )}
            />
            <div className="min-w-0 flex-1">
              <Primitive.Title className="text-sm font-medium text-fg">
                {entry.title}
              </Primitive.Title>
              {entry.description ? (
                <Primitive.Description className="mt-0.5 text-xs text-fg-muted">
                  {entry.description}
                </Primitive.Description>
              ) : null}
            </div>
            {entry.action ? (
              <Primitive.Action
                altText={entry.action.label}
                onClick={entry.action.onClick}
                className="rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-accent-soft"
              >
                {entry.action.label}
              </Primitive.Action>
            ) : null}
          </Primitive.Root>
        );
      })}
      <Primitive.Viewport className="fixed right-4 bottom-10 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
    </Primitive.Provider>
  );
}
